import React, { useState, useEffect, useMemo, useRef } from "react";
import { ArrowLeft, Save, Plus, MousePointer2, Hand, RotateCcw, Settings2, Loader2, Send, Minus, Play, AlertTriangle, Bot, MessageCircle, MessageSquare, Mail, ChevronDown } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "../../components/CustomToast";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  ReactFlowProvider,
  BackgroundVariant,
  Panel,
  useReactFlow,
  useViewport,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import {
  getWorkflow, updateWorkflow, publishWorkflow,
  WorkflowNode, WorkflowEdge as ApiEdge,
  WorkflowCondition, getAgentCurrentVersion,
} from "./api";
import type { WorkflowVersion } from "./api";
import { useWorkflowAgents } from "./hooks/use-workflow-agents";
import { WorkflowVersionsMenu } from "./components/WorkflowVersionsMenu";
import { cn } from "../../lib/utils";
import { useCanvasPalette } from "../../lib/theme";
import { InspectorPanel } from "./components/panels/InspectorPanel";
import { CanvasSelectionVisibility } from "./components/CanvasSelectionVisibility";
import { useWorkflowStore, AppNode, AppEdge } from "./store";
import { StartNode } from "./nodes/StartNode";
import { AgentNode } from "./nodes/AgentNode";
import { EndNode } from "./nodes/EndNode";
import { EmailNode } from "./nodes/EmailNode";
import { MessageNode } from "./nodes/MessageNode";
import { ConditionEdge } from "./nodes/ConditionEdge";
import { WORKFLOW_STEP_SPACING } from "./layout";

const nodeTypes = {
  start: StartNode,
  agent: AgentNode,
  end: EndNode,
  email: EmailNode,
  whatsapp: MessageNode,
  sms: MessageNode,
};

// React Flow node types that send a message instead of dialing an agent.
const ACTION_NODE_TYPES = new Set(["email", "whatsapp", "sms"]);

const ACTION_NODE_DEFAULTS: Record<string, Record<string, any>> = {
  whatsapp: {
    nodeLabel: "Send WhatsApp",
    whatsapp: { message_kind: "free_text", message_body: "", recipient_source: "customer_phone", recipient_field: "", template_id: "", template_name: "", template_mapping: {}, ai_agent_handoff: false, ai_agent_id: "", whatsapp_instance_name: "" },
  },
  sms: {
    nodeLabel: "Send SMS",
    sms: { message_body: "", recipient_source: "customer_phone", recipient_field: "" },
  },
  email: {
    nodeLabel: "Send Email",
    email: { to: "", cc: [], subject: "", body: "" },
  },
};

const edgeTypes = {
  conditionEdge: ConditionEdge,
};

// ── Serialise local state → API payload ────────────────────────
type VersionInfo = { version_id: string; version_name: string };

function serializeNodes(
  nodes: AppNode[],
  agents: any[] = [],
  versionMap: Record<string, VersionInfo> = {},
): WorkflowNode[] {
  return nodes.map(n => {
    // An action node (email / whatsapp / sms) dials nothing: it carries a
    // template/message instead of an agent + version, and the server rejects
    // it at publish if that is blank.
    if (n.type && ACTION_NODE_TYPES.has(n.type)) {
      const kind = n.type as "email" | "whatsapp" | "sms";
      const base = {
        node_id: n.id,
        node_type: kind,
        agent_id: "",
        agent_name: "",
        version_id: null,
        version_name: "",
        label: (n.data?.nodeLabel as string) || "",
        position: { x: n.position.x, y: n.position.y },
        email: null as any,
        whatsapp: null as any,
        sms: null as any,
      };
      if (kind === "email") {
        const email = (n.data?.email as any) || {};
        base.email = {
          to: String(email.to || ""),
          cc: Array.isArray(email.cc) ? email.cc.map(String) : [],
          subject: String(email.subject || ""),
          body: String(email.body || ""),
        };
      } else {
        const msg = (n.data?.[kind] as any) || {};
        base[kind] = {
          message_kind: kind === "whatsapp" && msg.message_kind === "template" ? "template" : "free_text",
          message_body: String(msg.message_body || ""),
          recipient_source: msg.recipient_source === "outfield" ? "outfield" : "customer_phone",
          recipient_field: String(msg.recipient_field || ""),
          ...(kind === "whatsapp"
            ? {
                ai_agent_handoff: Boolean(msg.ai_agent_handoff),
                ai_agent_id: String(msg.ai_agent_id || ""),
                whatsapp_instance_name: String(msg.whatsapp_instance_name || ""),
                template_id: String(msg.template_id || ""),
                template_name: String(msg.template_name || ""),
                template_mapping:
                  msg.template_mapping && typeof msg.template_mapping === "object"
                    ? Object.fromEntries(
                        Object.entries(msg.template_mapping).map(([k, v]) => [String(k), String(v || "")]),
                      )
                    : {},
              }
            : {}),
        };
      }
      return base as unknown as WorkflowNode;
    }
    const agentId = (n.data?.agentName as string) || "";
    const agent = agents.find(a => a.agent_id === agentId);
    const resolved = versionMap[agentId];
    // A version pinned on the node wins; otherwise use the agent's current
    // version that the studio resolved for the outfield schema.
    const pinnedId = (n.data?.versionId as string) || "";
    const versionId = pinnedId || resolved?.version_id || "";
    const versionName =
      (n.data?.versionName as string) || resolved?.version_name || "";
    return {
      node_id: n.id,
      agent_id: agentId,
      agent_name: agent?.name || agent?.agent_name || "",
      // version_id is nullable server-side; version_name / agent_name are plain
      // strings there, so never send null for them.
      version_id: versionId || null,
      version_name: versionName || "",
      label: (n.data?.nodeLabel as string) || "",
      position: { x: n.position.x, y: n.position.y },
      node_type: "call" as const,
      email: null,
      whatsapp: null,
      sms: null,
    };
  });
}

/** A delay the operator enabled but never filled in. */
export function isIncompleteDelay(delay: any): boolean {
  if (!delay) return false;
  const value = Number(delay.value);
  return !Number.isFinite(value) || value < 1;
}

function serializeEdges(edges: AppEdge[]): ApiEdge[] {
  // Priorities the operator set are sent as-is. They are only rewritten when
  // a source node's branches would be rejected by the backend's
  // duplicate_edge_priority check (duplicate or non-numeric), and even then the
  // rewrite is order-preserving — so nobody has to fill the field in by hand.
  const bySource = new Map<string, AppEdge[]>();
  edges.forEach(e => {
    const list = bySource.get(e.source) || [];
    list.push(e);
    bySource.set(e.source, list);
  });

  const priorityById = new Map<string, number>();
  bySource.forEach(list => {
    const seen = new Set<number>();
    const conflicted = list.some(e => {
      const p = Number(e.data?.priority);
      if (!Number.isInteger(p) || p < 0 || seen.has(p)) return true;
      seen.add(p);
      return false;
    });

    if (!conflicted) {
      list.forEach(e => priorityById.set(e.id, Number(e.data?.priority)));
      return;
    }

    [...list]
      .sort((a, b) => {
        const pa = Number(a.data?.priority);
        const pb = Number(b.data?.priority);
        const na = Number.isFinite(pa) ? pa : Number.MAX_SAFE_INTEGER;
        const nb = Number.isFinite(pb) ? pb : Number.MAX_SAFE_INTEGER;
        return na !== nb ? na - nb : String(a.id).localeCompare(String(b.id));
      })
      .forEach((e, index) => priorityById.set(e.id, index));
  });

  return edges.map(e => {
    const delay = e.data?.delay as any;
    return {
      edge_id: e.id,
      from_node_id: e.source,
      to_node_id: e.target,
      priority: priorityById.get(e.id) ?? 0,
      // Strip out empty/template conditions (field must be non-empty)
      conditions: ((e.data?.conditions as WorkflowCondition[]) || []).filter(
        (c: any) => c.field && String(c.field).trim() !== ""
      ),
      // An unfilled delay is blocked before we get here; never send a partial
      // one, since the backend rejects value < 1 for the whole request.
      delay: delay && !isIncompleteDelay(delay) ? delay : undefined,
    };
  });
}

// ── API graph → canvas state ──────────────────────────────────
// Shared by the initial draft load and by "view this version", so both
// produce identical node/edge shapes.
function mapApiNode(n: WorkflowNode, i: number): AppNode {
  const position = { x: n.position?.x || (100 + i * 300), y: n.position?.y || 200 };

  // An action node keeps its own card even in first position — unlike a call
  // node it is never the "start" (dialing) node; the backend walks past it to
  // find the agent the first leg dials.
  if (n.node_type && n.node_type !== "call") {
    const kind = n.node_type;
    const data: Record<string, any> = {
      nodeLabel: n.label || (kind === "email" ? "Send Email" : kind === "sms" ? "Send SMS" : "Send WhatsApp"),
    };
    if (kind === "email") {
      data.email = {
        to: n.email?.to || "",
        cc: n.email?.cc || [],
        subject: n.email?.subject || "",
        body: n.email?.body || "",
      };
    } else {
      const msg = (n as any)[kind] || {};
      data[kind] = {
        message_kind: kind === "whatsapp" && msg.message_kind === "template" ? "template" : "free_text",
        message_body: msg.message_body || "",
        recipient_source: msg.recipient_source === "outfield" ? "outfield" : "customer_phone",
        recipient_field: msg.recipient_field || "",
        ...(kind === "whatsapp"
          ? {
              ai_agent_handoff: Boolean(msg.ai_agent_handoff),
              ai_agent_id: String(msg.ai_agent_id || ""),
              whatsapp_instance_name: String(msg.whatsapp_instance_name || ""),
              template_id: String(msg.template_id || ""),
              template_name: String(msg.template_name || msg.template_snapshot?.name || ""),
              template_mapping:
                msg.template_mapping && typeof msg.template_mapping === "object" ? { ...msg.template_mapping } : {},
              template_snapshot: msg.template_snapshot || undefined,
            }
          : {}),
      };
    }
    return { id: n.node_id, type: kind, position, data };
  }

  return {
    id: n.node_id,
    type: i === 0 ? 'start' : 'agent',
    position,
    data: {
      agentName: n.agent_id || "",
      nodeLabel: n.label || n.agent_name || (i === 0 ? "First Call" : "New Step"),
      versionId: n.version_id || "",
      versionName: n.version_name || "",
    },
  };
}

function mapApiEdge(e: ApiEdge): AppEdge {
  return {
    id: e.edge_id || `${e.from_node_id}-${e.to_node_id}`,
    source: e.from_node_id,
    target: e.to_node_id,
    type: 'conditionEdge',
    data: {
      priority: e.priority ?? 0,
      conditions: e.conditions || [],
      delay: e.delay ?? null,
    },
  };
}

/**
 * The agent id(s) of the nearest call step(s) upstream of `nodeId`, walking
 * back through the graph and stepping *past* other action nodes (an email /
 * whatsapp / sms step produces no outfields of its own). These are the agents
 * whose outfields an action step's `{{token}}`s may legitimately reference.
 */
function nearestUpstreamAgentIds(nodeId: string, nodes: AppNode[], edges: AppEdge[]): string[] {
  const byId = new Map(nodes.map(n => [n.id, n]));
  const seen = new Set<string>([nodeId]);
  const queue = edges.filter(e => e.target === nodeId).map(e => e.source);
  const agentIds = new Set<string>();

  while (queue.length) {
    const cur = queue.shift()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    const node = byId.get(cur);
    if (!node) continue;

    if (node.type === "agent" || node.type === "start") {
      const aid = String(node.data?.agentName || "").trim();
      if (aid) agentIds.add(aid);
      // Stop here — this call node's outfields are the freshest for that branch.
      continue;
    }
    // An upstream action node has nothing to offer; keep walking past it.
    edges.filter(e => e.target === cur).forEach(e => queue.push(e.source));
  }
  return [...agentIds];
}

// A stable fingerprint of the *meaningful* graph content. Used to tell a real
// operator edit from React Flow's own post-mount churn (node measurement,
// selection, viewport) — those change the nodes/edges array identity but not
// this signature, so they must not trip the "unsaved changes" guard.
function graphSignature(nodes: AppNode[], edges: AppEdge[]): string {
  const n = nodes
    .map(nd => ({
      id: nd.id,
      type: nd.type || "",
      x: Math.round(nd.position?.x ?? 0),
      y: Math.round(nd.position?.y ?? 0),
      agentName: (nd.data?.agentName as string) ?? "",
      nodeLabel: (nd.data?.nodeLabel as string) ?? "",
      versionId: (nd.data?.versionId as string) ?? "",
      // Action-node templates/messages — otherwise editing an email body or a
      // WhatsApp message never trips the "unsaved changes" guard.
      email: (nd.data?.email as any) ?? null,
      whatsapp: (nd.data?.whatsapp as any) ?? null,
      sms: (nd.data?.sms as any) ?? null,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
  const e = edges
    .map(ed => ({
      id: ed.id,
      source: ed.source,
      target: ed.target,
      priority: (ed.data?.priority as number) ?? 0,
      conditions: (ed.data?.conditions as any) ?? [],
      delay: (ed.data?.delay as any) ?? null,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify({ n, e });
}

type AddableNodeType = "agent" | "whatsapp" | "sms" | "email";

const ADD_MENU: { type: AddableNodeType; label: string; Icon: typeof Bot; iconClass: string }[] = [
  { type: "agent", label: "Agent (call)", Icon: Bot, iconClass: "text-ink-3" },
  { type: "whatsapp", label: "WhatsApp message", Icon: MessageCircle, iconClass: "text-[#25D366]" },
  { type: "sms", label: "SMS message", Icon: MessageSquare, iconClass: "text-[#2563EB]" },
  { type: "email", label: "Email", Icon: Mail, iconClass: "text-[#EA4335]" },
];

function TopToolbar({ onAddNode, isPanMode, setIsPanMode }: { onAddNode: (type: AddableNodeType) => void, isPanMode: boolean, setIsPanMode: (v: boolean) => void }) {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const { zoom } = useViewport();
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!addOpen) return;
    const close = (e: MouseEvent) => {
      if (addRef.current && !addRef.current.contains(e.target as globalThis.Node)) setAddOpen(false);
    };
    document.addEventListener("mousedown", close, true);
    return () => document.removeEventListener("mousedown", close, true);
  }, [addOpen]);

  return (
    <Panel position="top-center" className="mt-4 pointer-events-auto">
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center gap-1.5 rounded-full bg-elevated px-3 py-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-line"
      >
        <div className="flex items-center gap-1">
          <button onClick={() => zoomOut({ duration: 300 })} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-soft text-ink-3 transition-colors" title="Zoom Out">
            <Minus className="h-4 w-4" />
          </button>
          <span className="text-[13px] font-extrabold text-ink-2 w-12 text-center select-none" title="Zoom Level">{Math.round(zoom * 100)}%</span>
          <button onClick={() => zoomIn({ duration: 300 })} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-soft text-ink-3 transition-colors" title="Zoom In">
            <Plus className="h-4 w-4" />
          </button>
        </div>
        
        <div className="w-[1px] h-6 bg-line mx-1" />
        
        <div className="flex items-center gap-1">
          <button 
            onClick={() => setIsPanMode(false)}
            className={cn(
              "h-8 w-8 rounded-full flex items-center justify-center transition-colors",
              !isPanMode ? "bg-brand-soft text-brand" : "hover:bg-soft text-ink-3"
            )}
            title="Select Mode"
          >
            <MousePointer2 className="h-4 w-4" />
          </button>
          <button 
            onClick={() => setIsPanMode(true)}
            className={cn(
              "h-8 w-8 rounded-full flex items-center justify-center transition-colors",
              isPanMode ? "bg-brand-soft text-brand" : "hover:bg-soft text-ink-3"
            )}
            title="Pan Mode"
          >
            <Hand className="h-[18px] w-[18px]" />
          </button>
          <button 
            onClick={() => fitView({ duration: 500 })}
            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-soft text-ink-3 transition-colors"
            title="Fit View"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
        
        <div className="w-[1px] h-6 bg-line mx-1" />

        <div className="relative" ref={addRef}>
          <button
            onClick={() => setAddOpen(v => !v)}
            className="flex items-center gap-1.5 px-3 h-8 rounded-full hover:bg-brand-soft text-brand transition-colors"
            title="Add a step"
          >
            <Plus className="h-4 w-4" />
            <span className="text-[12.5px] font-bold">Add Step</span>
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {addOpen && (
            <div className="absolute right-0 top-full mt-2 w-52 rounded-xl border border-line bg-elevated py-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.12)] z-50">
              {ADD_MENU.map(({ type, label, Icon, iconClass }) => (
                <button
                  key={type}
                  onClick={() => { setAddOpen(false); onAddNode(type); }}
                  className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[12.5px] font-medium text-ink-2 hover:bg-soft hover:text-brand transition-colors"
                >
                  <Icon className={cn("h-3.5 w-3.5", iconClass)} />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
}

function ReactFlowWrapper({ workflow, agents, onUpdateWorkflow }: { workflow: any, agents: any[], onUpdateWorkflow: (patch: any) => void }) {
  const store = useWorkflowStore();
  const { fitView } = useReactFlow();
  const palette = useCanvasPalette();

  const [mapOpen, setMapOpen] = useState(false);
  const [isPanMode, setIsPanMode] = useState(true);

  const queryClient = useQueryClient();

  // Persistence is manual now: the draft is written only by the Save button
  // and by Publish (which saves first). No background auto-save.

  // Merge errors and agent labels into node/edge data so the custom components can render them
  const nodesWithData = useMemo(() => {
    // 1. Compute Step Index using BFS graph traversal
    const stepIndices = new Map<string, number>();
    const startNode = store.nodes.find(n => n.type === 'start');
    let currentStepNum = 1;

    if (startNode) {
      const queue = [startNode.id];
      const visited = new Set<string>([startNode.id]);
      
      while (queue.length > 0) {
        const currId = queue.shift()!;
        
        // Find all edges extending FROM currId, sorting by priority desc for stable numbering
        const childEdges = store.edges.filter(e => e.source === currId);
        childEdges.sort((a, b) => {
           const pA = (a.data?.priority as number) || 0;
           const pB = (b.data?.priority as number) || 0;
           return pB - pA;
        });
        
        for (const edge of childEdges) {
          const childId = edge.target;
          if (!visited.has(childId)) {
            visited.add(childId);
            const childNode = store.nodes.find(n => n.id === childId);
            if (childNode && childNode.type === 'agent') {
              stepIndices.set(childId, currentStepNum++);
            }
            queue.push(childId);
          }
        }
      }
    }
    
    // For any unlinked agent nodes, assign them remaining numbers
    store.nodes.forEach(node => {
      if (node.type === 'agent' && !stepIndices.has(node.id)) {
        stepIndices.set(node.id, currentStepNum++);
      }
    });

    return store.nodes.map(node => {
      const agent = agents.find(a => a.agent_id === node.data.agentName);
      
      return {
        ...node,
        data: {
          ...node.data,
          agentLabel: agent?.name || agent?.agent_name || node.data.agentName,
          hasError: !!store.validationErrors[node.id],
          stepIndex: stepIndices.get(node.id) || null,
          availableAgents: agents, // Pass agents down
        }
      };
    });
  }, [store.nodes, store.edges, store.validationErrors, agents]);

  const edgesWithData = useMemo(() => {
    return store.edges.map(edge => ({
      ...edge,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 15,
        height: 15,
        color: store.validationErrors[edge.id]
          ? palette.edgeError
          : edge.id === store.selectedId
          ? palette.edgeSelected
          : palette.edge,
      },
      data: {
        ...edge.data,
        hasError: !!store.validationErrors[edge.id],
      }
    }));
  }, [store.edges, store.validationErrors, store.selectedId, palette]);

  // New steps go to the RIGHT of the rightmost node, following the graph's
  // left-to-right flow — stacking them straight down ran them off the bottom
  // of the viewport after a few adds.
  const nextNodePosition = () => {
    if (store.nodes.length === 0) return { x: 200, y: 200 };
    const rightmost = store.nodes.reduce((prev, current) =>
      current.position.x > prev.position.x ? current : prev
    );
    return { x: rightmost.position.x + WORKFLOW_STEP_SPACING, y: rightmost.position.y };
  };

  // Pan/zoom so the freshly added node is on screen. A brand-new node has no
  // measured width yet on the very next frame, which made fitView compute the
  // wrong zoom/pan (squashed the gap) until something else forced a re-fit.
  // Two frames gives React Flow time to measure it first.
  const revealCanvas = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => fitView({ duration: 300, maxZoom: 1, padding: 0.3 }));
    });
  };

  const handleAddNode = () => {
    const newNodeId = `node-${Date.now()}`;
    store.setNodes([...store.nodes, {
      id: newNodeId,
      type: 'agent',
      position: nextNodePosition(),
      data: { agentName: "", nodeLabel: "New Step" }
    }]);

    store.setSelectedId(newNodeId);
    store.setInspectorOpen(false);
    revealCanvas();
  };

  const handleAddActionNode = (kind: "email" | "whatsapp" | "sms") => {
    const newNodeId = `node-${Date.now()}`;
    store.setNodes([...store.nodes, {
      id: newNodeId,
      type: kind,
      position: nextNodePosition(),
      data: { ...ACTION_NODE_DEFAULTS[kind] },
    }]);

    store.setSelectedId(newNodeId);
    store.setInspectorOpen(false);
    revealCanvas();
  };

  const selectedNode = nodesWithData.find(n => n.id === store.selectedId) || null;
  const selectedEdge = edgesWithData.find(e => e.id === store.selectedId) || null;

  // Branches out of the selected edge's source, in the order they're checked.
  // Priority numbers are derived from this order on save, never typed.
  const siblingBranches = selectedEdge
    ? store.edges
        .filter(e => e.source === selectedEdge.source)
        .sort((a, b) => {
          const pa = (a.data?.priority as number) ?? 0;
          const pb = (b.data?.priority as number) ?? 0;
          return pa !== pb ? pa - pb : String(a.id).localeCompare(String(b.id));
        })
    : [];
  const branchIndex = siblingBranches.findIndex(e => e.id === selectedEdge?.id);

  const moveBranch = (delta: number) => {
    const target = branchIndex + delta;
    if (branchIndex < 0 || target < 0 || target >= siblingBranches.length) return;
    const reordered = [...siblingBranches];
    const [moved] = reordered.splice(branchIndex, 1);
    reordered.splice(target, 0, moved);
    // Write the new order back as dense priorities so the canvas and the next
    // save agree without the operator touching a number.
    const nextPriority = new Map(reordered.map((e, i) => [e.id, i]));
    store.setEdges(store.edges.map(e =>
      nextPriority.has(e.id)
        ? { ...e, data: { ...e.data, priority: nextPriority.get(e.id) } }
        : e
    ));
  };

  return (
    <div className="absolute inset-0 flex overflow-hidden">
      <div className="relative min-w-0 flex-1">
      <ReactFlow
        nodes={nodesWithData}
        edges={edgesWithData}
        onNodesChange={store.onNodesChange}
        onEdgesChange={store.onEdgesChange}
        onConnect={store.onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={(_, node) => { store.setSelectedId(node.id); store.setInspectorOpen(true); }}
        onEdgeClick={(_, edge) => { store.setSelectedId(edge.id); store.setInspectorOpen(true); }}
        onPaneClick={() => { store.setSelectedId(null); store.setInspectorOpen(false); }}
        panOnDrag={isPanMode}
        selectionOnDrag={!isPanMode}
        proOptions={{ hideAttribution: true }}
        fitView
        // Never zoom past 100% on open — a single small start node would
        // otherwise get magnified to fitView's default 2x.
        fitViewOptions={{ maxZoom: 1, padding: 0.3 }}
        minZoom={0.01}
      >
        <CanvasSelectionVisibility inspectorOpen={store.inspectorOpen} selectedId={store.selectedId} />
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color={palette.dots} />
        
        <TopToolbar
          onAddNode={(type) => type === "agent" ? handleAddNode() : handleAddActionNode(type)}
          isPanMode={isPanMode}
          setIsPanMode={setIsPanMode}
        />
      </ReactFlow>
      </div>

      {store.inspectorOpen && (
        <InspectorPanel
          selectedNode={selectedNode as any}
          selectedEdge={selectedEdge as any}
          agents={agents}
          outfieldsMap={store.outfieldsMap}
          infieldsMap={store.infieldsMap}
          sourceAgentId={selectedEdge ? store.nodes.find(n => n.id === selectedEdge.source)?.data?.agentName as string : null}
          upstreamAgentIds={selectedNode ? nearestUpstreamAgentIds(selectedNode.id, store.nodes, store.edges) : []}
          branchOrder={branchIndex >= 0 ? branchIndex + 1 : 0}
          branchCount={siblingBranches.length}
          onMoveEdge={moveBranch}
          workflow={workflow}
          onUpdateWorkflow={onUpdateWorkflow}
          onClose={() => store.setInspectorOpen(false)}
          onUpdateNode={(patch) => store.updateNodeData(store.selectedId!, patch)}
          onUpdateEdge={(patch) => store.updateEdgeData(store.selectedId!, patch)}
          onDeleteEdge={() => {
            store.setEdges(store.edges.filter(e => e.id !== store.selectedId));
            store.setSelectedId(null);
          }}
        />
      )}
    </div>
  );
}

export function AutomationStudio() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const workflowId = searchParams.get("id");
  const store = useWorkflowStore();

  // Current agent version (id + name) per agent_id, resolved from the same
  // call the outfield schema is fetched with. Frozen into the node on save.
  const [versionMap, setVersionMap] = useState<Record<string, VersionInfo>>({});
  // The draft has edits not yet written to the DB (no auto-save anymore).
  const [dirty, setDirty] = useState(false);
  // Signature of the graph as last loaded/saved. The draft is "dirty" only when
  // the live graph's signature differs from this.
  const baselineRef = React.useRef<string>("");
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  // Non-null while a past published snapshot is loaded on the canvas instead
  // of the live draft.
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  // Inline rename on the header title — the Config panel also has a Name
  // field (debounced, saved via the same mutation), but that requires opening
  // Config first. This is the direct, discoverable path.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  const updateMutation = useMutation({
    mutationFn: (payload: any) => updateWorkflow(workflowId!, payload),
    onSuccess: () => {
      // Whatever was on the canvas is now the draft — re-baseline to it.
      const { nodes, edges } = useWorkflowStore.getState();
      baselineRef.current = graphSignature(nodes, edges);
      setDirty(false);
      setViewingVersion(null);
      queryClient.invalidateQueries({ queryKey: ["call-workflows"] });
      queryClient.invalidateQueries({ queryKey: ["call-workflow", workflowId] });
      queryClient.invalidateQueries({ queryKey: ["call-workflow-versions", workflowId] });
    },
  });
  const { mutate: mutateWorkflow, mutateAsync: mutateWorkflowAsync } = updateMutation;

  const publishMutation = useMutation({
    mutationFn: publishWorkflow,
    onSuccess: () => {
      // Publish saves the draft first, so the canvas is the source of truth now.
      const { nodes, edges } = useWorkflowStore.getState();
      baselineRef.current = graphSignature(nodes, edges);
      setDirty(false);
      store.setValidationErrors({});
      queryClient.invalidateQueries({ queryKey: ["call-workflows"] });
      queryClient.invalidateQueries({ queryKey: ["call-workflow", workflowId] });
      toast.success("Workflow published successfully!");
    },
    onError: (err: any) => {
      store.setValidationErrors({ global: err.message });
      if (err?.response?.data?.errors) {
        const newErrors: Record<string, string> = {};
        err.response.data.errors.forEach((e: any) => {
          if (e.edge_id) newErrors[e.edge_id] = e.message;
          else if (e.node_id) newErrors[e.node_id] = e.message;
        });
        store.setValidationErrors(newErrors);
      }
    }
  });

  const { data: workflow, isLoading } = useQuery({
    queryKey: ["call-workflow", workflowId],
    queryFn: () => getWorkflow(workflowId!),
    enabled: !!workflowId,
  });

  const { agents } = useWorkflowAgents();

  const buildSavePayload = () => {
    const { nodes, edges } = useWorkflowStore.getState();
    const serialized = serializeNodes(nodes, agents, versionMap);
    const startNode = nodes.find(n => n.type === 'start');
    const startNodeId = startNode ? startNode.id : (serialized[0]?.node_id || "start");
    return {
      start_node_id: startNodeId,
      nodes: serialized,
      edges: serializeEdges(edges),
    };
  };

  /**
   * Client-side blockers, keyed by node/edge id so the canvas highlights them.
   * Only rules the operator can act on directly; the backend still runs the
   * full graph validation at publish.
   */
  const collectSaveBlockingIssues = (): Record<string, string> => {
    const { edges } = useWorkflowStore.getState();
    const issues: Record<string, string> = {};
    edges.forEach(e => {
      if (isIncompleteDelay(e.data?.delay)) {
        issues[e.id] = "This branch has a delay enabled but no duration. Set a value or turn the delay off.";
      }
    });
    if (Object.keys(issues).length > 0) {
      issues.global = "A branch has an incomplete delay.";
    }
    return issues;
  };

  const collectPublishBlockingIssues = (): Record<string, string> => {
    const { edges, nodes } = useWorkflowStore.getState();
    const issues = collectSaveBlockingIssues();
    const startNode = nodes.find(n => n.type === 'start');
    const startNodeHasAgent = !!String(startNode?.data?.agentName || "").trim();
    const actionNodeIds = new Set(
      nodes.filter(n => n.type && ACTION_NODE_TYPES.has(n.type)).map(n => n.id),
    );

    edges.forEach(e => {
      const isLegacyTriggerEdge = e.source === startNode?.id && !startNodeHasAgent;
      // An edge into an email / whatsapp / sms step places no call, so the
      // "call-to-call needs a delay" rule does not apply — mirrors the server.
      const targetsAction = actionNodeIds.has(e.target);
      if (!isLegacyTriggerEdge && !targetsAction && !e.data?.delay) {
        issues[e.id] = "Add a delay before publishing this branch.";
      }
    });
    if (Object.keys(issues).some(key => key !== "global")) {
      issues.global = "Every call-to-call branch needs a delay before publish.";
    }
    return issues;
  };

  const commitRename = () => {
    const trimmed = nameDraft.trim();
    setEditingName(false);
    if (!trimmed) {
      toast.error("Workflow name cannot be blank");
      return;
    }
    if (trimmed === (workflow?.name || "")) return;
    updateMutation.mutate(
      { name: trimmed },
      {
        onSuccess: () => toast.success("Workflow renamed"),
        // The base mutation has no onError, so a failed rename here would
        // otherwise fail silently — the field just reverts on the next
        // refetch with no indication why.
        onError: (e: any) => toast.error(e?.message || "Rename failed"),
      }
    );
  };

  const handleSave = () => {
    if (!workflowId) return;
    const issues = collectSaveBlockingIssues();
    if (Object.keys(issues).length > 0) {
      store.setValidationErrors(issues);
      toast.error("Fix the highlighted branch before saving.");
      return;
    }
    store.setValidationErrors({});
    mutateWorkflow(buildSavePayload(), {
      onSuccess: () => toast.success("Workflow saved"),
      onError: (e: any) => toast.error(e?.message || "Save failed"),
    });
  };

  const handlePublish = async () => {
    if (!workflowId) return;
    const issues = collectPublishBlockingIssues();
    if (Object.keys(issues).length > 0) {
      store.setValidationErrors(issues);
      toast.error("Fix the highlighted branch before publishing.");
      return;
    }
    store.setValidationErrors({});
    try {
      // Flush the draft first — Publish freezes whatever is in the DB.
      await mutateWorkflowAsync(buildSavePayload());
    } catch (e: any) {
      store.setValidationErrors({ global: e?.message || "Couldn't save before publishing." });
      return;
    }
    publishMutation.mutate(workflowId);
  };

  const hasLoaded = React.useRef(false);

  useEffect(() => {
    if (workflow && !hasLoaded.current) {
      hasLoaded.current = true;
      const loadedNodes: AppNode[] = (workflow.nodes || []).map(mapApiNode);

      if (loadedNodes.length === 0) {
        // Legacy workflow saved before the dialog seeded a start agent.
        const startId = (workflow as any).start_node_id || "start";
        loadedNodes.push({
          id: startId,
          type: 'start',
          position: { x: 200, y: 200 },
          data: { agentName: "", nodeLabel: "First Call" }
        });
      }

      const loadedEdges: AppEdge[] = (workflow.edges || []).map(mapApiEdge);
      // Set the baseline before touching the store so the subscription below
      // sees "no divergence" for this load.
      baselineRef.current = graphSignature(loadedNodes, loadedEdges);
      store.setNodes(loadedNodes);
      store.setEdges(loadedEdges);
      setDirty(false);

      // Open the inspector on the first step straight away — a fresh workflow
      // opens on an unconfigured start step, and the pane is where the agent
      // is picked.
      const firstNode = loadedNodes[0];
      if (firstNode) {
        store.setSelectedId(firstNode.id);
        store.setInspectorOpen(true);
      }
    }
  }, [workflow, store]);

  // Load a published snapshot onto the canvas. It becomes the working copy —
  // Save writes it back to the draft, the next Publish freezes it as a new
  // version. "Back to draft" reloads the real draft.
  const loadVersionIntoCanvas = (v: WorkflowVersion) => {
    // The live version is what the draft was synced to at publish time, so
    // loading it is not "restoring an old snapshot" — don't warn as if it were.
    const isLive = v.workflow_version === workflow?.workflow_version;
    const nodes = (v.nodes || []).map(mapApiNode);
    const edges = (v.edges || []).map(mapApiEdge);
    // Live version = same as the saved draft, so it becomes the new baseline.
    if (isLive) baselineRef.current = graphSignature(nodes, edges);
    store.setNodes(nodes);
    store.setEdges(edges);
    store.setSelectedId(null);
    store.setInspectorOpen(false);
    store.setValidationErrors({});
    setViewingVersion(v.workflow_version);
    setDirty(!isLive);
    toast.success(
      isLive
        ? `Loaded v${v.workflow_version} — this is the live version.`
        : `Loaded v${v.workflow_version} onto the canvas — Save to keep it as the draft.`,
    );
  };

  const backToDraft = () => {
    if (!workflow) return;
    const nodes = (workflow.nodes || []).map(mapApiNode);
    if (nodes.length === 0) {
      const startId = (workflow as any).start_node_id || "start";
      nodes.push({ id: startId, type: 'start', position: { x: 200, y: 200 }, data: { agentName: "", nodeLabel: "First Call" } });
    }
    const edges = (workflow.edges || []).map(mapApiEdge);
    baselineRef.current = graphSignature(nodes, edges);
    store.setNodes(nodes);
    store.setEdges(edges);
    store.setSelectedId(null);
    store.setInspectorOpen(false);
    setViewingVersion(null);
    setDirty(false);
  };

  // No auto-save: mark the draft dirty when the graph's *content* diverges from
  // the last loaded/saved baseline. Comparing the signature (not the array
  // identity) means React Flow's post-mount node measurement / selection /
  // viewport updates — which replace the arrays but change nothing meaningful —
  // don't raise a false "unsaved changes" warning.
  useEffect(() => {
    const unsub = useWorkflowStore.subscribe((s, prev) => {
      if (!hasLoaded.current) return;
      if (s.nodes === prev.nodes && s.edges === prev.edges) return;
      setDirty(graphSignature(s.nodes, s.edges) !== baselineRef.current);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const leaveStudio = () => {
    if (dirty) {
      setShowLeaveDialog(true);
      return;
    }
    navigate("/home/automation");
  };

  const saveThenLeave = async () => {
    const issues = collectSaveBlockingIssues();
    if (Object.keys(issues).length > 0) {
      store.setValidationErrors(issues);
      setShowLeaveDialog(false);
      toast.error("Fix the highlighted branch before saving.");
      return;
    }
    try {
      await mutateWorkflowAsync(buildSavePayload());
      setShowLeaveDialog(false);
      navigate("/home/automation");
    } catch (e: any) {
      setShowLeaveDialog(false);
      toast.error(e?.message || "Save failed");
    }
  };

  useEffect(() => {
    const fetchMeta = async () => {
      const agentIds = Array.from(new Set(store.nodes.map(n => n.data.agentName as string).filter(Boolean)));
      for (const aid of agentIds) {
        if (store.outfieldsMap[aid] && versionMap[aid]) continue;
        try {
          const version = await getAgentCurrentVersion(aid);
          if (!store.outfieldsMap[aid]) {
            store.setOutfieldsMap(prev => ({ ...prev, [aid]: version.call_outcomes || [] }));
          }
          store.setInfieldsMap(prev => (prev[aid] ? prev : { ...prev, [aid]: version.infields || [] }));
          setVersionMap(prev => prev[aid]
            ? prev
            : { ...prev, [aid]: { version_id: version.version_id || "", version_name: version.version_name || "" } });
        } catch (e) {}
      }
    };
    fetchMeta();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.nodes, store.outfieldsMap, store.setOutfieldsMap]);

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center bg-soft">Loading canvas...</div>;
  }

  const workflowName = workflow?.name || "Untitled Workflow";
  const workflowStatus = workflow?.status || "DRAFT";

  return (
    <div className="flex h-screen w-full flex-col bg-canvas">
      {/* Header */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-line bg-elevated px-4 z-10 shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={leaveStudio} className="rounded-md p-1.5 text-ink-3 hover:bg-soft hover:text-ink transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              {editingName ? (
                <input
                  autoFocus
                  value={nameDraft}
                  maxLength={200}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.currentTarget.blur(); }
                    else if (e.key === "Escape") { setEditingName(false); }
                  }}
                  className="rounded border border-brand bg-elevated px-1.5 py-0.5 text-sm font-bold text-ink outline-none focus:ring-1 focus:ring-brand"
                />
              ) : (
                <h1
                  className="cursor-text rounded px-1 -mx-1 text-sm font-bold text-ink hover:bg-soft"
                  title="Click to rename"
                  onClick={() => { setNameDraft(workflowName); setEditingName(true); }}
                >
                  {workflowName}
                </h1>
              )}
              {/* Status only — version history lives in the Versions button. */}
              <span className={cn(
                "rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                workflowStatus === "draft" ? "bg-warning-soft text-warning" : "bg-success-soft text-success"
              )}>
                {workflowStatus === "draft" ? "DRAFT" : `PUBLISHED v${workflow?.workflow_version || 1}`}
              </span>
            </div>
            <p className="font-mono text-[10px] text-ink-3">
              {workflowStatus === "published" ? "Use workflow · calls follow this graph" : "calls use the agent's config"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(`/home/automation/runs?workflowId=${workflowId}`)}
            className="text-xs font-semibold text-ink-2 hover:text-ink flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line bg-elevated hover:bg-soft shadow-2xs transition-colors"
          >
            <Play className="w-3 h-3 text-success fill-success" /> View Runs
          </button>

          {workflowId && (
            <WorkflowVersionsMenu
              workflowId={workflowId}
              currentVersion={workflow?.workflow_version}
              status={workflowStatus}
              onSelectVersion={loadVersionIntoCanvas}
              triggerVariant="button"
              align="right"
            />
          )}

          <button
            onClick={() => { store.setSelectedId(null); store.setInspectorOpen(true); }}
            className="text-sm font-medium text-ink-3 hover:text-ink flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-soft"
          >
            <Settings2 className="w-4 h-4" /> Config
          </button>

          {dirty && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-warning" title="Unsaved changes">
              <span className="h-1.5 w-1.5 rounded-full bg-warning" /> Unsaved
            </span>
          )}
          <div className="h-4 w-px bg-line mx-1" />
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending || publishMutation.isPending}
            className="text-sm font-medium text-ink-2 bg-soft hover:bg-line flex items-center gap-1.5 px-4 py-1.5 rounded-md transition-colors disabled:opacity-50"
          >
            {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          <button
            onClick={handlePublish}
            disabled={publishMutation.isPending || updateMutation.isPending}
            className="flex items-center gap-1.5 rounded-md bg-success px-4 py-1.5 text-sm font-semibold text-white hover:bg-success transition-colors shadow-sm disabled:opacity-50"
          >
            {(publishMutation.isPending || updateMutation.isPending) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Publish
          </button>
        </div>
      </header>

      {/* Viewing a loaded version */}
      {viewingVersion !== null && (
        viewingVersion === workflow?.workflow_version ? (
          <div className="bg-success-soft border-b border-success/30 px-4 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 z-10 relative">
            <span className="text-success font-semibold text-sm">
              Viewing v{viewingVersion} — the live version.
            </span>
            <button
              onClick={backToDraft}
              className="rounded-md border border-success/40 bg-elevated px-2.5 py-1 text-xs font-semibold text-success hover:bg-success-soft transition-colors"
            >
              Back to draft
            </button>
          </div>
        ) : (
          <div className="bg-brand-soft border-b border-brand/30 px-4 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 z-10 relative">
            <span className="text-brand font-semibold text-sm">
              Viewing v{viewingVersion} — this is a past snapshot, not the current draft.
            </span>
            <span className="text-brand text-xs">Save to make it the working draft.</span>
            <button
              onClick={backToDraft}
              className="rounded-md border border-brand/40 bg-elevated px-2.5 py-1 text-xs font-semibold text-brand hover:bg-brand-soft transition-colors"
            >
              Back to draft
            </button>
          </div>
        )
      )}

      {/* Top Validation Banner (Fail Loud) */}
      {Object.keys(store.validationErrors).length > 0 && (
        <div className="bg-danger-soft border-b border-danger/30 px-4 py-2 flex items-center justify-center gap-2 z-10 relative">
          <span className="text-danger font-semibold text-sm">Cannot publish: validation errors detected.</span>
          <span className="text-danger text-xs">{store.validationErrors.global || "Please fix the highlighted steps below."}</span>
        </div>
      )}

      <div className="flex min-h-0 flex-1 relative bg-soft">
        <ReactFlowProvider>
          <ReactFlowWrapper
            workflow={workflow}
            agents={agents}
            onUpdateWorkflow={(patch) =>
              updateMutation.mutate(patch, {
                // No base onError on this mutation — without a per-call one, a
                // failed Config-panel edit (name/description/max_steps) fails
                // silently and the field just reverts on the next refetch.
                onError: (e: any) => toast.error(e?.message || "Update failed"),
              })
            }
          />
        </ReactFlowProvider>
      </div>

      {/* Unsaved changes dialog */}
      {showLeaveDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="w-full max-w-md rounded-2xl bg-elevated p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warning-soft border border-warning/30 text-warning">
                <AlertTriangle className="h-4.5 w-4.5" />
              </span>
              <div>
                <h3 className="text-base font-bold text-ink">Unsaved changes</h3>
                <p className="mt-1 text-sm text-ink-3">
                  You've edited <strong className="text-ink-2">{workflowName}</strong> since your
                  last save. Save your changes before leaving the studio?
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-end gap-2.5 pt-4 border-t border-line">
              <button
                onClick={() => setShowLeaveDialog(false)}
                className="rounded-xl border border-line px-4 py-2 text-xs font-semibold text-ink-2 hover:bg-soft transition-colors"
              >
                Keep editing
              </button>
              <button
                onClick={() => { setShowLeaveDialog(false); navigate("/home/automation"); }}
                className="rounded-xl border border-danger/30 bg-danger-soft px-4 py-2 text-xs font-semibold text-danger hover:bg-danger-soft transition-colors"
              >
                Discard changes
              </button>
              <button
                onClick={saveThenLeave}
                disabled={updateMutation.isPending}
                className="rounded-xl bg-success px-5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-success disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
              >
                {updateMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save & leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
